import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"

import EditIcon from "@/assets/icons/edit/medium.svg?react"
import { Button } from "@/components/ds/Button"
import { InputFormField } from "@/components/ds/Input"
import { Typography } from "@/components/ds/Typography"
import { ImageUploadZone } from "@/components/shared/ImageUploadZone"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import Modal from "@/components/ui/modal"
import Toast from "@/components/ui/toast"
import { useUser } from "@/hooks/useUser"
import router from "@/routes/router"
import { AppDispatch } from "@/store/store"
import { clearUser, setUser } from "@/store/user/userSlice"
import { useDeleteMeMutation, useUpdateMeMutation } from "@/store/userAPI"

type ProfileMode = "view" | "edit"

const generateInitials = (firstName: string | null, lastName: string | null): string => {
  const first = firstName?.[0]?.toUpperCase() ?? ""
  const last = lastName?.[0]?.toUpperCase() ?? ""
  return `${first}${last}` || "?"
}

// ─── View Mode ───────────────────────────────────────────────────────────────

function ProfileViewMode({ onEdit }: { onEdit: () => void }) {
  const { user } = useUser()
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Row 1: Avatar + Name + Edit button */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-6">
          <Avatar className="h-[200px] w-[200px] shrink-0 rounded-xl bg-neutral-border">
            <AvatarImage src={user.image ?? ""} alt={t("profile")} className="object-cover" />
            <AvatarFallback className="rounded-xl bg-neutral-border text-subheading font-medium text-neutral-title">
              {generateInitials(user.firstName, user.lastName)}
            </AvatarFallback>
          </Avatar>

          <Typography variant="subheading" className="font-medium text-neutral-title">
            {user.firstName} {user.lastName}
          </Typography>
        </div>

        <Button variant="primary" size="medium" onClick={onEdit}>
          <EditIcon className="w-5" /> {t("editInformation")}
        </Button>
      </div>

      {/* Row 2: Info cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-2xl border border-neutral-border p-4">
          <Typography variant="body2" className="mb-2 text-neutral-text">
            {t("email")}
          </Typography>
          <Typography variant="body1" className="text-neutral-title">
            {user.email ?? "—"}
          </Typography>
        </div>
        <div className="rounded-2xl border border-neutral-border p-4">
          <Typography variant="body2" className="mb-2 text-neutral-text">
            {t("jobTitle")}
          </Typography>
          <Typography variant="body1" className="text-neutral-title">
            {user.jobTitle || "—"}
          </Typography>
        </div>
      </div>
    </div>
  )
}

// ─── Edit Mode ────────────────────────────────────────────────────────────────

const formSchema = z.object({
  fullName: z.string().min(1, "Required"),
  email: z.string().email("Invalid email"),
  jobTitle: z.string().optional(),
  image: z.string().optional(),
})

type ProfileFormData = z.infer<typeof formSchema>

function ProfileEditMode({ onSave }: { onSave: () => void }) {
  const { user } = useUser()
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const [updateMe, { isLoading: isSaving }] = useUpdateMeMutation()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteMe, { isLoading: isDeleting }] = useDeleteMeMutation()

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: [user.firstName, user.lastName].filter(Boolean).join(" "),
      email: user.email ?? "",
      jobTitle: user.jobTitle ?? "",
      image: user.image ?? "",
    },
  })

  const imageValue = form.watch("image")

  const handleSave = form.handleSubmit(async (data) => {
    const nameParts = data.fullName.trim().split(/\s+/)
    const firstName = nameParts[0] ?? ""
    const lastName = nameParts.slice(1).join(" ") || ""

    try {
      const result = await updateMe({
        firstName,
        lastName,
        email: data.email,
        jobTitle: data.jobTitle,
        image: data.image,
      }).unwrap()

      if (result.success) {
        dispatch(
          setUser({
            ...user,
            firstName,
            lastName,
            email: data.email,
            jobTitle: data.jobTitle ?? null,
            image: data.image || user.image,
          }),
        )
        Toast({ type: "success", message: t("profileUpdatedSuccessfully") })
        onSave()
      } else {
        Toast({ type: "error", message: result.error ?? t("errorUpdatingProfile") })
      }
    } catch {
      Toast({ type: "error", message: t("errorUpdatingProfile") })
    }
  })

  const handleDeleteAccount = async () => {
    try {
      await deleteMe().unwrap()
      dispatch(clearUser())
      router.navigate("/login")
    } catch {
      Toast({ type: "error", message: t("errorUpdatingProfile") })
      setDeleteModalOpen(false)
    }
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      {/* Edit section */}
      <div className="rounded-2xl border border-neutral-border p-6">
        {/* Avatar upload row */}
        <div className="mb-6 flex gap-6">
          <ImageUploadZone
            value={imageValue ?? ""}
            onChange={(url) => form.setValue("image", url)}
            alt={t("profile")}
          />

          {/* Upload instructions */}
          <div className="flex items-center">
            <Typography variant="body1" className="whitespace-pre-line text-neutral-text">
              {t("imageUpload.profilePicture")}
            </Typography>
          </div>
        </div>

        {/* Form fields */}
        <FormProvider {...form}>
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <InputFormField name="fullName" label={t("fullName")} placeholder={t("fullNamePlaceholder")} />
              <InputFormField name="email" label={t("email")} placeholder={t("email")} />
            </div>
            <InputFormField name="jobTitle" label={t("jobTitle")} placeholder={t("jobTitlePlaceholder")} />
          </div>
        </FormProvider>

        {/* Save button */}
        <div className="mt-6 flex justify-end">
          <Button variant="primary" size="medium" onClick={handleSave} disabled={isSaving}>
            {isSaving ? t("saving") : t("saveChanges")}
          </Button>
        </div>
      </div>

      {/* Delete account section */}
      <div className="rounded-2xl border border-neutral-border p-6">
        <div className="flex items-center justify-between gap-6">
          <div>
            <Typography variant="subheading" className="mb-1 font-medium text-neutral-title">
              {t("deleteYourAccount")}
            </Typography>
            <Typography variant="body1" className="text-neutral-text">
              {t("deleteYourAccountDescription")}
            </Typography>
          </div>
          <Button
            type="button"
            variant="outline"
            size="medium"
            className="hover:border-danger-hover hover:text-danger-hover shrink-0 border-danger-primary text-danger-primary hover:bg-danger-secondBg"
            onClick={() => setDeleteModalOpen(true)}
          >
            {t("deleteAccount")}
          </Button>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title={t("deleteAccount")} size="md">
        <Typography variant="body1" className="text-neutral-text">
          {t("deleteAccountConfirmation")}
        </Typography>
        <div className="-mx-6 mt-6 flex justify-between border-t border-neutral-border px-6 pt-5">
          <Button variant="primary" size="medium" onClick={() => setDeleteModalOpen(false)} disabled={isDeleting}>
            {t("cancel")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="medium"
            className="hover:border-danger-hover hover:text-danger-hover border-danger-primary text-danger-primary hover:bg-danger-secondBg"
            onClick={handleDeleteAccount}
            disabled={isDeleting}
          >
            {isDeleting ? t("deleting") : t("deleteAccount")}
          </Button>
        </div>
      </Modal>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ProfileQuickView() {
  const [mode, setMode] = useState<ProfileMode>("view")
  const { t } = useTranslation()

  return (
    <div className="flex h-full w-[1050px] max-w-[100%] flex-col">
      {/* Header */}
      <div className="border-b p-6">
        <h1 className="text-2xl font-medium leading-8 text-neutral-title">{t("profile")}</h1>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {mode === "view" ? (
          <ProfileViewMode onEdit={() => setMode("edit")} />
        ) : (
          <ProfileEditMode onSave={() => setMode("view")} />
        )}
      </div>
    </div>
  )
}
